import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import PublicHeader from '../components/PublicHeader';
import Footer from '../components/Footer';
import axios from 'axios';
import { API_BASE_URL, BASE_URL } from '../config/config';

/**
 * AI Concept Explainer
 * Provides deep explanations of complex concepts with analogies and examples
 */
const AIConceptExplainer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logFeatureUsage } = useSubscription();

  // Form state
  const [concept, setConcept] = useState('');
  const [subject, setSubject] = useState('');
  const [level, setLevel] = useState('intermediate');
  const [specificQuestions, setSpecificQuestions] = useState('');

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);

  /**
   * Generate explanation using AI
   */
  const handleExplain = useCallback(async (e) => {
    e.preventDefault();
    
    if (!concept.trim()) {
      setError('Please enter a concept to explain');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/ai-subscription/features/concept-explainer/explain`,
        {
          user_id: user.id,
          concept: concept.trim(),
          subject: subject.trim(),
          level: level,
          specific_questions: specificQuestions.trim()
        }
      );

      if (response.data.success) {
        const newExplanation = {
          ...response.data.data,
          concept: concept.trim(),
          timestamp: new Date()
        };
        
        setExplanation(newExplanation);
        
        // Add to history
        setHistory(prev => [newExplanation, ...prev].slice(0, 5));
        
        // Log feature usage
        await logFeatureUsage('concept_explain', {
          concept: concept.trim(),
          level: level
        });
      } else {
        setError(response.data.message || 'Failed to generate explanation');
      }
    } catch (err) {
      console.error('Concept explanation error:', err);
      setError(
        err.response?.data?.message || 
        'Failed to generate explanation. Please try again.'
      );
    } finally {
      setIsGenerating(false);
    }
  }, [concept, subject, level, specificQuestions, user, logFeatureUsage]);

  /**
   * Load explanation from history
   */
  const loadFromHistory = useCallback((item) => {
    setConcept(item.concept);
    setExplanation(item);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /**
   * Reset form
   */
  const handleReset = useCallback(() => {
    setConcept('');
    setSubject('');
    setLevel('intermediate');
    setSpecificQuestions('');
    setExplanation(null);
    setError('');
  }, []);

  /**
   * Format explanation text with proper styling
   */
  const formatExplanation = (text) => {
    if (!text) return '';
    
    return text.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('## ')) {
        return (
          <h3 key={index} className="text-xl font-bold text-gray-800 mt-6 mb-3 flex items-center">
            <span className="w-1 h-6 bg-blue-500 mr-3 rounded"></span>
            {line.replace('## ', '')}
          </h3>
        );
      }
      
      if (line.startsWith('### ')) {
        return (
          <h4 key={index} className="text-lg font-semibold text-gray-700 mt-4 mb-2">
            {line.replace('### ', '')}
          </h4>
        );
      }
      
      // Bold text
      if (line.includes('**')) {
        const formatted = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-blue-700">$1</strong>');
        return (
          <p key={index} className="mb-2 text-gray-700" dangerouslySetInnerHTML={{ __html: formatted }} />
        );
      }
      
      // Bullet points
      if (line.trim().startsWith('- ')) {
        return (
          <li key={index} className="ml-6 mb-2 text-gray-700 flex items-start">
            <span className="text-blue-500 mr-2 font-bold">•</span>
            <span>{line.trim().substring(2)}</span>
          </li>
        );
      }
      
      // Numbered lists
      if (/^\d+\.\s/.test(line.trim())) {
        return (
          <li key={index} className="ml-6 mb-2 text-gray-700 list-decimal">
            {line.trim().replace(/^\d+\.\s/, '')}
          </li>
        );
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <br key={index} />;
      }
      
      // Regular text
      return (
        <p key={index} className="mb-3 text-gray-700 leading-relaxed">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <PublicHeader />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <button
            onClick={() => navigate('/ai/features')}
            className="inline-flex items-center text-purple-600 hover:text-purple-800 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to AI Features
          </button>
          
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            💡 AI Concept Explainer
          </h1>
          <p className="text-gray-600 text-lg">
            Get deep, intuitive explanations of any concept with analogies and real-world examples
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Sidebar - Input Form */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                What would you like to understand?
              </h2>

              <form onSubmit={handleExplain} className="space-y-4">
                {/* Concept Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Concept/Topic *
                  </label>
                  <input
                    type="text"
                    value={concept}
                    onChange={(e) => setConcept(e.target.value)}
                    placeholder="e.g., Quantum Entanglement, Recursion, Photosynthesis"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    disabled={isGenerating}
                    required
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Subject (Optional)
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g., Physics, Computer Science"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    disabled={isGenerating}
                  />
                </div>

                {/* Level */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Explanation Level
                  </label>
                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition"
                    disabled={isGenerating}
                  >
                    <option value="simple">Simple (ELI5)</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced (Technical)</option>
                  </select>
                </div>

                {/* Specific Questions */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Specific Questions (Optional)
                  </label>
                  <textarea
                    value={specificQuestions}
                    onChange={(e) => setSpecificQuestions(e.target.value)}
                    placeholder="Any specific aspect you want explained?"
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition resize-none"
                    disabled={isGenerating}
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Buttons */}
                <div className="space-y-2">
                  <button
                    type="submit"
                    disabled={isGenerating || !concept.trim()}
                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${
                      isGenerating || !concept.trim()
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
                    }`}
                  >
                    {isGenerating ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Explaining...
                      </span>
                    ) : (
                      '💡 Explain Concept'
                    )}
                  </button>

                  {explanation && (
                    <button
                      type="button"
                      onClick={handleReset}
                      className="w-full px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                    >
                      New Concept
                    </button>
                  )}
                </div>
              </form>

              {/* History */}
              {history.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Explanations</h3>
                  <div className="space-y-2">
                    {history.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => loadFromHistory(item)}
                        className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-purple-50 rounded-lg transition-colors text-sm"
                      >
                        <p className="font-medium text-gray-800 truncate">{item.concept}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(item.timestamp).toLocaleTimeString()}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main Content - Explanation Display */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              {!explanation && !isGenerating && (
                <div className="text-center py-16">
                  <div className="text-7xl mb-6">💡</div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">
                    Ready to Learn Something New?
                  </h3>
                  <p className="text-gray-600 text-lg mb-6">
                    Enter any concept you want to understand better
                  </p>
                  <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <div className="text-3xl mb-2">🧬</div>
                      <p className="text-sm font-semibold">Science</p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="text-3xl mb-2">💻</div>
                      <p className="text-sm font-semibold">Technology</p>
                    </div>
                    <div className="bg-pink-50 p-4 rounded-lg">
                      <div className="text-3xl mb-2">📐</div>
                      <p className="text-sm font-semibold">Mathematics</p>
                    </div>
                  </div>
                </div>
              )}

              {isGenerating && (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
                  <p className="text-gray-600 text-lg">Breaking down the concept...</p>
                  <p className="text-gray-400 text-sm mt-2">This may take a moment</p>
                </div>
              )}

              {explanation && (
                <div>
                  {/* Concept Header */}
                  <div className="mb-8 pb-6 border-b-2 border-purple-100">
                    <h2 className="text-3xl font-bold text-gray-800 mb-2">
                      {explanation.concept}
                    </h2>
                    {explanation.subject && (
                      <p className="text-purple-600 font-medium">
                        Subject: {explanation.subject}
                      </p>
                    )}
                  </div>

                  {/* Explanation Content */}
                  <div className="prose max-w-none">
                    {formatExplanation(explanation.explanation || explanation.explanation_text)}
                  </div>

                  {/* Metadata */}
                  <div className="mt-8 pt-6 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
                    <div>
                      <p>Level: <span className="font-medium capitalize">{explanation.level}</span></p>
                      {explanation.generated_at && (
                        <p className="mt-1">Generated on {new Date(explanation.generated_at).toLocaleString()}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-xs">Powered by Gemini AI</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 md:p-8">
          <h3 className="text-xl font-bold text-gray-800 mb-4">💡 Tips for Better Explanations</h3>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">🎯</div>
              <h4 className="font-semibold text-gray-800 mb-1">Be Specific</h4>
              <p className="text-sm text-gray-600">
                "Quantum Entanglement" is better than just "Quantum"
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">📚</div>
              <h4 className="font-semibold text-gray-800 mb-1">Add Context</h4>
              <p className="text-sm text-gray-600">
                Mention the subject for domain-specific explanations
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">🎓</div>
              <h4 className="font-semibold text-gray-800 mb-1">Right Level</h4>
              <p className="text-sm text-gray-600">
                Choose the explanation level that matches your understanding
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="text-2xl mb-2">❓</div>
              <h4 className="font-semibold text-gray-800 mb-1">Ask Questions</h4>
              <p className="text-sm text-gray-600">
                Add specific questions to focus the explanation
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AIConceptExplainer;
